import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import { BellIcon, BellOffIcon, MoreHorizontal } from "lucide-react";
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from "nuqs";

import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { useAuth } from "@/components/lib/auth";
import { Container } from "@/components/pieces/bootstrap";
import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import { Avatar } from "@/components/pieces/users/Avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  Maybe,
  NotificationPreferenceType,
  RoleplayRank,
  ScenarioAssignmentDisplayStatus,
  ScenarioAssignmentType,
  useLoggedInNavDataQuery,
  useSaveNotificationPreferencesMutation,
  useScenarioAssignmentsQuery,
  useScenarioAssignmentsStatisticQuery,
} from "@/graphql";
import { useDataTable } from "@/hooks/use-data-table";

import { AssignmentResultDrawer } from "./AssignmentResultDrawer";
import { AlertModal } from "./modals/AlertModal";
import { DeleteModal } from "./modals/DeleteModal";
import { EditModal } from "./modals/EditModal";
import { ScenarioAssignmentFilters } from "./ScenarioAssignmentFilters";
import { SCENARIO_ASSIGNMENT_STATUS_STYLES } from "./utils";

// Types for our assignment data - extending the GraphQL type with computed fields
interface ParticipantAssignment extends ScenarioAssignmentType {
  bestRank?: Maybe<RoleplayRank> | undefined;
  completedDate?: string;
}

const columnHelper = createColumnHelper<ParticipantAssignment>();

interface ScenarioAssignmentContentProps {
  pageLoading?: boolean;
  pageType: "scenarioDetail" | "roleplayDetail";
  scenarioId?: string; // For filtering by scenario when on roleplay detail page
}

export function ScenarioAssignmentContent({
  scenarioId,
  pageType,
}: ScenarioAssignmentContentProps) {
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assignmentResultDrawerOpen, setAssignmentResultDrawerOpen] =
    useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<ParticipantAssignment | null>(null);

  // Filter states using useQueryState for URL persistence
  const [selectedCollections, setSelectedCollections] = useQueryState(
    "collections",
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [selectedScenarios, setSelectedScenarios] = useQueryState(
    "scenarios",
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [selectedAssigners, setSelectedAssigners] = useQueryState(
    "assigners",
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [selectedAssignees, setSelectedAssignees] = useQueryState(
    "assignees",
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [selectedStatuses, setSelectedStatuses] = useQueryState(
    "statuses",
    parseAsArrayOf(parseAsString).withDefault([]),
  );

  // Global search is now managed in the filters component, but we need to access it for the query
  const [globalSearch] = useQueryState("search", parseAsString.withDefault(""));
  const [searchDebounce, setSearchDebounce] = useState("");

  const { currentWorkspace } = useAuth();
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));
  const [size] = useQueryState("perPage", parseAsInteger.withDefault(5));
  const [alertEnabled, setAlertEnabled] = useState<boolean>(true);
  const {
    data,
    loading: loggedInNavDataLoading,
    refetch: refetchLoggedInNavData,
  } = useLoggedInNavDataQuery();

  useEffect(() => {
    const enabled =
      data?.currentUser?.notificationPreferences?.some(
        (pref) =>
          pref.type === NotificationPreferenceType.WeeklyAssignmentDigest &&
          pref.email,
      ) || false;
    setAlertEnabled(enabled);
  }, [data]);
  // Debounce global search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(globalSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [globalSearch]);

  // Reset page to 1 when filters change from empty to having values
  const prevFiltersRef = useRef({
    collections: selectedCollections,
    scenarios: selectedScenarios,
    assigners: selectedAssigners,
    assignees: selectedAssignees,
    statuses: selectedStatuses,
    search: searchDebounce,
  });

  useEffect(() => {
    const currentFilters = {
      collections: selectedCollections,
      scenarios: selectedScenarios,
      assigners: selectedAssigners,
      assignees: selectedAssignees,
      statuses: selectedStatuses,
      search: searchDebounce,
    };

    // Check if any filter actually changed
    const filtersChanged =
      JSON.stringify(prevFiltersRef.current.collections) !==
        JSON.stringify(currentFilters.collections) ||
      JSON.stringify(prevFiltersRef.current.scenarios) !==
        JSON.stringify(currentFilters.scenarios) ||
      JSON.stringify(prevFiltersRef.current.assigners) !==
        JSON.stringify(currentFilters.assigners) ||
      JSON.stringify(prevFiltersRef.current.assignees) !==
        JSON.stringify(currentFilters.assignees) ||
      JSON.stringify(prevFiltersRef.current.statuses) !==
        JSON.stringify(currentFilters.statuses) ||
      prevFiltersRef.current.search !== currentFilters.search;

    if (filtersChanged && page !== 1) {
      setPage(1);
    }

    // Update the ref with current values
    prevFiltersRef.current = currentFilters;
  }, [
    selectedCollections,
    selectedScenarios,
    selectedAssigners,
    selectedAssignees,
    selectedStatuses,
    searchDebounce,
    page,
    setPage,
  ]);

  // GraphQL queries and mutations
  const {
    data: assignmentsData,
    loading: assignmentsLoading,
    refetch: refetchAssignments,
    previousData: previousAssignmentsData,
  } = useScenarioAssignmentsQuery({
    variables: {
      workspaceId: currentWorkspace?.id || "",
      ...(scenarioId && { scenarioId }),
      ...(selectedCollections.length > 0 && {
        collectionIds: selectedCollections,
      }),
      ...(selectedScenarios.length > 0 && { scenarioIds: selectedScenarios }),
      ...(selectedAssigners.length > 0 && { assignerIds: selectedAssigners }),
      ...(selectedAssignees.length > 0 && { assigneeIds: selectedAssignees }),
      ...(selectedStatuses.length > 0 && {
        displayStatuses: selectedStatuses as ScenarioAssignmentDisplayStatus[],
      }),
      ...(searchDebounce && { search: searchDebounce }),
      pageSize: size,
      page,
    },
    skip: !currentWorkspace?.id,
    fetchPolicy: "network-only",
  });

  // Fetch statistics (only when scenarioId is available)
  const { data: statisticsData, refetch: refetchStatistics } =
    useScenarioAssignmentsStatisticQuery({
      variables: {
        scenarioIds: scenarioId
          ? [scenarioId]
          : selectedScenarios.length > 0
            ? selectedScenarios
            : undefined,
        ...(selectedCollections.length > 0 && {
          collectionIds: selectedCollections,
        }),
        ...(selectedStatuses.length > 0 && {
          displayStatuses:
            selectedStatuses as ScenarioAssignmentDisplayStatus[],
        }),
        ...(selectedAssignees.length > 0 && {
          assigneeIds: selectedAssignees,
        }),
      },
      skip: !currentWorkspace?.id,
      fetchPolicy: "network-only",
    });

  // Use current data if available, otherwise fall back to previous data while loading
  const displayAssignmentsData = assignmentsData || previousAssignmentsData;

  // Transform GraphQL data to our component format
  const assignments: ParticipantAssignment[] = useMemo(() => {
    if (!displayAssignmentsData?.scenarioAssignments?.data) return [];

    return displayAssignmentsData.scenarioAssignments.data
      .filter(
        (assignment): assignment is ScenarioAssignmentType =>
          assignment !== null,
      )
      .map((assignment) => ({
        ...assignment,
        bestRank: (assignment.bestRank as RoleplayRank) || "Unranked",
        completedDate:
          assignment.displayStatus === ScenarioAssignmentDisplayStatus.Completed
            ? new Date(assignment.updatedAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : undefined,
      }));
  }, [displayAssignmentsData?.scenarioAssignments?.data]);

  // Get stats from API when available, otherwise calculate manually
  const stats = useMemo(() => {
    if (statisticsData?.scenarioAssignmentsStatistic) {
      // Use API statistics when available (when scenarioId is provided)
      const stats = statisticsData.scenarioAssignmentsStatistic;
      return [
        { status: "NOT_STARTED", count: stats.notStartedAssignments || 0 },
        { status: "IN_PROGRESS", count: stats.inProgressAssignments || 0 },
        { status: "PAST_DUE", count: stats.pastDueAssignments || 0 },
        { status: "COMPLETED", count: stats.completedAssignments || 0 },
        { status: "DID_NOT_PASS", count: stats.didNotPassAssignments || 0 },
      ];
    } else {
      // Fallback to manual calculation when API stats not available
      const notStarted = assignments.filter(
        (a) => a.displayStatus === ScenarioAssignmentDisplayStatus.NotStarted,
      ).length;
      const pastDue = assignments.filter(
        (a) => a.displayStatus === ScenarioAssignmentDisplayStatus.PastDue,
      ).length;
      const complete = assignments.filter(
        (a) => a.displayStatus === ScenarioAssignmentDisplayStatus.Completed,
      ).length;
      const inProgress = assignments.filter(
        (a) => a.displayStatus === ScenarioAssignmentDisplayStatus.InProgress,
      ).length;
      const didNotPass = assignments.filter(
        (a) => a.displayStatus === ScenarioAssignmentDisplayStatus.DidNotPass,
      ).length;

      return [
        { status: "NOT_STARTED", count: notStarted },
        { status: "IN_PROGRESS", count: inProgress },
        { status: "PAST_DUE", count: pastDue },
        { status: "COMPLETED", count: complete },
        { status: "DID_NOT_PASS", count: didNotPass },
      ];
    }
  }, [statisticsData, assignments]);

  const [saveNotificationPreferences] =
    useSaveNotificationPreferencesMutation();
  const handleAlertSave = useCallback((enabled: boolean) => {
    saveNotificationPreferences({
      variables: {
        input: {
          weeklyAssignmentDigestEnabled: enabled,
        },
      },
    }).then(() => {
      refetchLoggedInNavData();
    });
  }, []);

  const handleEditAssignment = useCallback(
    (assignment: ParticipantAssignment) => {
      setSelectedAssignment(assignment);
      setEditModalOpen(true);
    },
    [],
  );

  const handleAlertClick = useCallback(() => {
    setAlertModalOpen(true);
  }, []);

  const handleDeleteAssignment = useCallback(
    (assignment: ParticipantAssignment) => {
      setSelectedAssignment(assignment);
      setDeleteModalOpen(true);
    },
    [],
  );

  const handleDeleteSuccess = useCallback(async () => {
    // Refetch assignments and statistics to get updated data
    await refetchAssignments();
    await refetchStatistics();
    setDeleteModalOpen(false);
    setSelectedAssignment(null);
  }, [refetchAssignments, scenarioId]);

  const columns = useMemo(
    () => [
      ...(pageType === "roleplayDetail"
        ? [
            columnHelper.accessor("scenario", {
              id: "scenario",
              header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Scenario" />
              ),
              cell: ({ getValue }) => (
                <div
                  className="font-medium"
                  style={{
                    width: "250px",
                    whiteSpace: "normal",
                  }}
                >
                  {getValue()?.name || ""}
                </div>
              ),
            }),
          ]
        : []),

      columnHelper.accessor("assignee", {
        id: "assignedTo",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Assigned To" />
        ),
        cell: ({ getValue }) => {
          const assignee = getValue();
          if (!assignee) return null;

          return (
            <div className="flex items-center gap-2 min-w-[200px]">
              <Avatar
                user={{
                  name: assignee.name || "",
                  photo: assignee.photo,
                  email: assignee.email,
                }}
                size={24}
              />
              <span
                className="font-medium"
                style={{
                  whiteSpace: "normal",
                }}
                title={assignee.name || assignee.email || ""}
              >
                {assignee.name || assignee.email || ""}
              </span>
            </div>
          );
        },
      }),

      columnHelper.accessor("createdAt", {
        id: "createdAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Assigned" />
        ),
        cell: ({ getValue }) => {
          const date = getValue();
          return (
            <div className="text-md">
              {new Date(date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </div>
          );
        },
      }),

      columnHelper.accessor("dueDate", {
        id: "due",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Due" />
        ),
        cell: ({ getValue, row }) => {
          const dueDate = getValue();
          const displayStatus = row.original.displayStatus;

          return (
            <div className="text-md">
              {displayStatus === ScenarioAssignmentDisplayStatus.PastDue &&
              dueDate ? (
                <span className="text-yellow-800 font-medium">
                  {new Date(dueDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              ) : dueDate ? (
                <span>
                  {new Date(dueDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              ) : (
                <span className="text-gray-400">–</span>
              )}
            </div>
          );
        },
      }),

      columnHelper.accessor("displayStatus", {
        id: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ getValue, row }) => {
          const status = getValue();
          if (!status) return null;

          const config = SCENARIO_ASSIGNMENT_STATUS_STYLES[status];
          const completedDate = row.original.completedDate;

          const statusBadge = (
            <div className="flex items-center py-1 rounded-sm gap-2 w-fit">
              {config.icon}
              <div style={{ color: config.textColor }}>{config.text}</div>
            </div>
          );

          // Show tooltip with completed date only for completed assignments
          if (
            status === ScenarioAssignmentDisplayStatus.Completed &&
            completedDate
          ) {
            return (
              <SimpleTooltip content={completedDate} side="top">
                {statusBadge}
              </SimpleTooltip>
            );
          }

          return statusBadge;
        },
      }),

      columnHelper.accessor("bestScore", {
        id: "bestScore",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Best Score" />
        ),
        cell: ({ getValue, row }) => {
          const score = getValue();
          const sessionCount = row.original.sessionCount;
          if (!sessionCount || sessionCount < 1) {
            return <span className="text-gray-400">–</span>;
          }

          const scoreContent = (
            <div className="flex items-center gap-1">
              <div className="flex items-center">
                <RankIcon rank={row.original.bestRank} size={24} />
              </div>
              <span className="font-medium">{Math.round(score ?? 0)}%</span>
            </div>
          );

          // Show tooltip with session count if available
          if (sessionCount && sessionCount > 0) {
            return (
              <SimpleTooltip
                content={`${sessionCount} session${sessionCount > 1 ? "s" : ""}`}
                side="top"
              >
                {scoreContent}
              </SimpleTooltip>
            );
          }

          return scoreContent;
        },
      }),

      columnHelper.display({
        id: "actions",
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => (
          <div
            className="flex justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-gray-200"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {row.original.displayStatus !==
                  ScenarioAssignmentDisplayStatus.Completed && (
                  <DropdownMenuItem
                    onClick={() => handleEditAssignment(row.original)}
                  >
                    Edit
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => handleDeleteAssignment(row.original)}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      }),
    ],
    [pageType, handleEditAssignment, handleDeleteAssignment],
  );

  const { table } = useDataTable({
    data: assignments as ParticipantAssignment[],
    columns,
    pageCount: Math.ceil(
      (displayAssignmentsData?.scenarioAssignments?.total ?? 0) / (size ?? 1),
    ),
    enableHiding: false,
    getRowId: (row) => row?.id || "",
    initialState: {
      pagination: {
        pageIndex: page - 1,
        pageSize: size || 5,
      },
      columnPinning: { right: ["actions"] },
    },
    enableSorting: false,
  });

  return (
    <>
      <Container px={0}>
        <div className="space-y-6 p-8 bg-white mt-8 rounded-lg border outline-neutral-200">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <div className="text-lg font-semibold text-gray-900">
                  Direct Assignments
                </div>
                <div className="text-sm text-gray-600 bg-gray-100 rounded-sm w-6 h-6 flex items-center justify-center">
                  {displayAssignmentsData?.scenarioAssignments?.total ??
                    assignments.length}
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Roleplays assigned directly to users outside of programs
              </p>
            </div>
            {!loggedInNavDataLoading && (
              <Button
                intent="secondary"
                variant="outline"
                className="gap-2 bg-gray-100 border-gray-200"
                onClick={handleAlertClick}
              >
                {alertEnabled ? (
                  <BellIcon className="h-4 w-4" />
                ) : (
                  <BellOffIcon className="h-4 w-4" />
                )}
                Alerts
              </Button>
            )}
          </div>

          {/* Status Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {stats.map(({ status, count }) => {
              const config =
                SCENARIO_ASSIGNMENT_STATUS_STYLES[
                  status as keyof typeof SCENARIO_ASSIGNMENT_STATUS_STYLES
                ];
              return (
                <Card
                  className="p-0 border-none"
                  key={status}
                  style={{ backgroundColor: config.backgroundColor }}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-3">
                        {config.icon}
                        <span
                          className="text-base"
                          style={{ color: config.textColor }}
                        >
                          {config.text}
                        </span>
                      </div>
                      <div
                        className="text-3xl font-semibold"
                        style={{ color: config.textColor }}
                      >
                        {count}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-4">
            <ScenarioAssignmentFilters
              workspaceId={currentWorkspace?.id || ""}
              pageType={pageType}
              selectedCollections={selectedCollections}
              selectedScenarios={selectedScenarios}
              selectedAssigners={selectedAssigners}
              selectedAssignees={selectedAssignees}
              selectedStatuses={selectedStatuses}
              onCollectionsChange={setSelectedCollections}
              onScenariosChange={setSelectedScenarios}
              onAssignersChange={setSelectedAssigners}
              onAssigneesChange={setSelectedAssignees}
              onStatusesChange={setSelectedStatuses}
            />
          </div>

          {/* Data Table */}
          <div className="relative">
            <DataTable
              table={table}
              totalRecords={
                displayAssignmentsData?.scenarioAssignments?.total ?? 0
              }
              pagination={true}
              showSelectedRowCount={false}
              onRowClick={(row: ParticipantAssignment) => {
                setSelectedAssignment(row);
                setAssignmentResultDrawerOpen(true);
              }}
              emptyState={
                <div className="relative min-h-[330px] justify-center items-center flex">
                  {assignmentsLoading ? (
                    <div>
                      <div className="flex items-center justify-center text-sm text-gray-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                        Loading assignments...
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-center text-sm text-gray-600">
                        No assignments
                      </div>
                    </div>
                  )}
                </div>
              }
            />
          </div>
        </div>
      </Container>

      {/* Modals */}
      <AlertModal
        open={alertModalOpen}
        onOpenChange={setAlertModalOpen}
        onSave={handleAlertSave}
        isEnabled={alertEnabled}
      />

      {selectedAssignment && (
        <EditModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          assignment={selectedAssignment}
          onSuccess={() => {
            refetchAssignments();
            refetchStatistics();
          }}
        />
      )}

      <DeleteModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onSuccess={handleDeleteSuccess}
        assignmentId={selectedAssignment?.id}
        assignmentName={selectedAssignment?.scenario?.name || undefined}
        participantName={selectedAssignment?.assignee?.name || undefined}
        dueDate={
          selectedAssignment?.dueDate
            ? new Date(selectedAssignment.dueDate)
            : undefined
        }
      />

      <AssignmentResultDrawer
        assignmentId={selectedAssignment?.id ?? ""}
        open={assignmentResultDrawerOpen}
        onOpenChange={setAssignmentResultDrawerOpen}
        onPrevClick={() => {
          if (!selectedAssignment) return;
          const prevIndex = assignments.findIndex(
            (a) => a.id === selectedAssignment?.id,
          );
          if (prevIndex > 0) {
            setSelectedAssignment(assignments[prevIndex - 1]);
          }
        }}
        onNextClick={() => {
          if (!selectedAssignment) return;
          const nextIndex = assignments.findIndex(
            (a) => a.id === selectedAssignment?.id,
          );
          if (nextIndex < assignments.length - 1) {
            setSelectedAssignment(assignments[nextIndex + 1]);
          }
        }}
      />
    </>
  );
}
